// Churchill DSCR Calculation Engine
// Implements full LLPA pricing logic from rate sheets

import {
  CHURCHILL_PROGRAMS,
  DSCR_BASE_LLPA,
  DSCR_ADVANTAGE_LLPA,
  DSCR_PLUS_LLPA,
  LOCK_PERIOD_ADJUSTMENTS,
  EXTENSION_FEES,
  FIXED_FEES,
  STATE_TIERS,
} from '../data/churchillRateData';

// ============================================================================
// CONSTANTS & OPTIONS
// ============================================================================

export const PROPERTY_TYPES = {
  SINGLE_FAMILY: 'Single Family',
  SINGLE_FAMILY_TOWNHOME: 'Single Family / Townhome',
  CONDO: 'Condo',
  TWO_TO_FOUR_UNITS: '2-4 Unit',
  SHORT_TERM_RENTAL: 'Short Term Rental',
  MULTI_FAMILY_5_9: 'Multi Family (5-9)',
  MULTI_FAMILY_5_10: 'Multi Family (5-10)',
};

export const LOAN_PURPOSES = {
  PURCHASE: 'Purchase',
  RATE_TERM_REFI: 'Rate/Term Refinance',
  CASH_OUT_REFI: 'Cash-Out Refinance',
};

export const AMORTIZATION_TYPES = {
  FULLY_AMORTIZING: 'Fully Amortizing',
  INTEREST_ONLY: 'Interest Only',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the LLPA configuration for a specific program
 */
export const getLLPAConfig = (programName) => {
  switch (programName) {
    case 'DSCR Base':
      return DSCR_BASE_LLPA;
    case 'DSCR Advantage':
      return DSCR_ADVANTAGE_LLPA;
    case 'DSCR Plus':
      return DSCR_PLUS_LLPA;
    default:
      return DSCR_BASE_LLPA;
  }
};

/**
 * Get the rate matrix for a specific program and product type
 */
export const getRateMatrix = (programName, productType, customRateSheet = null) => {
  // If custom rate sheet provided (from George's uploads), use that
  if (customRateSheet && customRateSheet.data) {
    return customRateSheet.data.map(row => ({
      rate: parseFloat(row.Rate || row.rate || 0),
      price: parseFloat(row.Price || row.price || 0),
    }));
  }
  
  // Otherwise use pre-populated data
  const program = CHURCHILL_PROGRAMS[programName];
  if (!program || !program.rates.rateMatrix[productType]) {
    return [];
  }
  return program.rates.rateMatrix[productType];
};

/**
 * Get available product types for a program
 */
export const getProductTypes = (programName) => {
  const program = CHURCHILL_PROGRAMS[programName];
  return program ? program.rates.productTypes : [];
};

/**
 * Calculate LTV
 */
export const calculateLTV = (loanAmount, propertyValue) => {
  if (!propertyValue || propertyValue === 0) return 0;
  return (loanAmount / propertyValue) * 100;
};

/**
 * Calculate DSCR from inputs
 */
export const calculateDSCR = (monthlyRent, monthlyPITIA) => {
  if (!monthlyPITIA || monthlyPITIA === 0) return 0;
  return monthlyRent / monthlyPITIA;
};

/**
 * Calculate Monthly P&I Payment
 */
export const calculateMonthlyPI = (loanAmount, interestRate, termYears = 30) => {
  if (!loanAmount || !interestRate) return 0;
  
  const monthlyRate = (interestRate / 100) / 12;
  const numPayments = termYears * 12;
  
  if (monthlyRate === 0) return loanAmount / numPayments;
  
  const payment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                  (Math.pow(1 + monthlyRate, numPayments) - 1);
  
  return payment;
};

/**
 * Calculate Interest-Only Payment
 */
export const calculateMonthlyIO = (loanAmount, interestRate) => {
  if (!loanAmount || !interestRate) return 0;
  return (loanAmount * (interestRate / 100)) / 12;
};

/**
 * Get FICO tier from numeric score
 */
export const getFICOTier = (ficoScore) => {
  const score = parseInt(ficoScore) || 0;
  if (score >= 780) return '780+';
  if (score >= 760) return '760-779';
  if (score >= 740) return '740-759';
  if (score >= 720) return '720-739';
  if (score >= 700) return '700-719';
  if (score >= 680) return '680-699';
  if (score >= 660) return '660-679';
  return '660-679'; // Minimum tier
};

/**
 * Get DSCR tier from ratio
 */
export const getDSCRTier = (dscrRatio) => {
  const ratio = parseFloat(dscrRatio) || 0;
  if (ratio >= 1.25) return '>=1.25';
  if (ratio >= 1.00) return '1.00-1.24';
  return '<1.00';
};

/**
 * Get Loan Amount tier
 */
export const getLoanAmountTier = (loanAmount) => {
  const amount = parseFloat(loanAmount) || 0;
  if (amount > 2500000) return '>2,500,000';
  if (amount > 2000000) return '2,000,001-2,500,000';
  if (amount > 1500000) return '>1,500,000';
  if (amount > 1000000) return '1,000,001-2,000,000';
  if (amount >= 250000) return '250,000-1,000,000';
  if (amount >= 150000) return '<250,000';
  return '<150,000';
};

/**
 * Get state tier for pricing
 */
export const getStateTier = (stateCode, programName) => {
  if (programName === 'DSCR Advantage') {
    return STATE_TIERS.advantageTier1.includes(stateCode) ? 'tier1' : 'tier2';
  }
  if (programName === 'DSCR Plus') {
    return STATE_TIERS.plusTier1.includes(stateCode) ? 'tier1' : 'tier2';
  }
  return 'tier1'; // DSCR Base doesn't have state tiers
};

// ============================================================================
// MAIN LLPA CALCULATION
// ============================================================================

/**
 * Calculate all LLPA adjustments for a given loan scenario
 * Returns detailed breakdown of each adjustment
 */
export const calculateLLPAAdjustments = (inputs, programName) => {
  const llpa = getLLPAConfig(programName);
  const adjustments = [];
  let totalAdjustment = 0;

  // 1. FICO Adjustment
  const ficoTier = getFICOTier(inputs.ficoScore);
  const ficoKey = programName === 'DSCR Base' ? 
    (ficoTier === '780+' ? '760+' : ficoTier.replace('780+', '760+').replace('760-779', '740-759')) : 
    ficoTier;
  
  // Find matching FICO key in LLPA
  let ficoAdjustment = 0;
  for (const [key, value] of Object.entries(llpa.fico || {})) {
    if (key === ficoTier || key === ficoKey) {
      ficoAdjustment = value;
      break;
    }
  }
  // Fallback to closest tier
  if (ficoAdjustment === 0 && llpa.fico) {
    const score = parseInt(inputs.ficoScore) || 0;
    if (score >= 780 && llpa.fico['780+'] !== undefined) ficoAdjustment = llpa.fico['780+'];
    else if (score >= 760 && llpa.fico['760+'] !== undefined) ficoAdjustment = llpa.fico['760+'];
    else if (score >= 760 && llpa.fico['760-779'] !== undefined) ficoAdjustment = llpa.fico['760-779'];
    else if (score >= 740 && llpa.fico['740-759'] !== undefined) ficoAdjustment = llpa.fico['740-759'];
    else if (score >= 720 && llpa.fico['720-739'] !== undefined) ficoAdjustment = llpa.fico['720-739'];
    else if (score >= 700 && llpa.fico['700-719'] !== undefined) ficoAdjustment = llpa.fico['700-719'];
    else if (score >= 680 && llpa.fico['680-699'] !== undefined) ficoAdjustment = llpa.fico['680-699'];
    else if (llpa.fico['660-679'] !== undefined) ficoAdjustment = llpa.fico['660-679'];
  }
  
  if (ficoAdjustment !== 0) {
    adjustments.push({
      category: 'FICO Score',
      description: `FICO ${ficoTier}`,
      adjustment: ficoAdjustment,
    });
    totalAdjustment += ficoAdjustment;
  }

  // 2. Property Type Adjustment
  const propertyType = inputs.propertyType || 'Single Family';
  let propertyAdjustment = llpa.propertyType?.[propertyType] || 0;
  
  // Try alternative property type names
  if (propertyAdjustment === 0 && llpa.propertyType) {
    if (propertyType.includes('Single Family') && llpa.propertyType['Single Family / Townhome'] !== undefined) {
      propertyAdjustment = llpa.propertyType['Single Family / Townhome'];
    } else if (propertyType === 'Single Family / Townhome' && llpa.propertyType['Single Family'] !== undefined) {
      propertyAdjustment = llpa.propertyType['Single Family'];
    }
  }
  
  if (propertyAdjustment !== 0) {
    adjustments.push({
      category: 'Property Type',
      description: propertyType,
      adjustment: propertyAdjustment,
    });
    totalAdjustment += propertyAdjustment;
  }

  // 3. Loan Purpose Adjustment
  const loanPurpose = inputs.loanPurpose || 'Purchase';
  const purposeAdjustment = llpa.loanPurpose?.[loanPurpose] || 0;
  if (purposeAdjustment !== 0) {
    adjustments.push({
      category: 'Loan Purpose',
      description: loanPurpose,
      adjustment: purposeAdjustment,
    });
    totalAdjustment += purposeAdjustment;
  }

  // 4. DSCR Adjustment
  const dscrTier = getDSCRTier(inputs.dscrRatio);
  const dscrAdjustment = llpa.dscr?.[dscrTier] || 0;
  if (dscrAdjustment !== 0) {
    adjustments.push({
      category: 'DSCR',
      description: `DSCR ${dscrTier}`,
      adjustment: dscrAdjustment,
    });
    totalAdjustment += dscrAdjustment;
  }

  // 5. Prepayment Penalty Adjustment
  const prepayPenalty = inputs.prepayPenalty || '36 Months';
  const prepayAdjustment = llpa.prepayPenalty?.[prepayPenalty] || 0;
  if (prepayAdjustment !== 0) {
    adjustments.push({
      category: 'Prepay Penalty',
      description: prepayPenalty,
      adjustment: prepayAdjustment,
    });
    totalAdjustment += prepayAdjustment;
  }

  // 6. Loan Amount Adjustment
  const loanAmountTier = getLoanAmountTier(inputs.loanAmount);
  let loanAmountAdjustment = 0;
  if (llpa.loanAmount) {
    for (const [key, value] of Object.entries(llpa.loanAmount)) {
      if (key === loanAmountTier) {
        loanAmountAdjustment = value;
        break;
      }
    }
    // Fallback logic
    if (loanAmountAdjustment === 0) {
      const amount = parseFloat(inputs.loanAmount) || 0;
      if (amount < 150000 && llpa.loanAmount['<150,000'] !== undefined) {
        loanAmountAdjustment = llpa.loanAmount['<150,000'];
      } else if (amount < 250000 && llpa.loanAmount['<250,000'] !== undefined) {
        loanAmountAdjustment = llpa.loanAmount['<250,000'];
      } else if (amount > 2500000 && llpa.loanAmount['>2,500,000'] !== undefined) {
        loanAmountAdjustment = llpa.loanAmount['>2,500,000'];
      } else if (amount > 2000000 && llpa.loanAmount['>2,000,000'] !== undefined) {
        loanAmountAdjustment = llpa.loanAmount['>2,000,000'];
      } else if (amount > 1500000 && llpa.loanAmount['>1,500,000'] !== undefined) {
        loanAmountAdjustment = llpa.loanAmount['>1,500,000'];
      } else if (amount > 1000000) {
        loanAmountAdjustment = llpa.loanAmount['1,000,001-2,000,000'] || llpa.loanAmount['>1,000,000'] || 0;
      }
    }
  }
  if (loanAmountAdjustment !== 0) {
    adjustments.push({
      category: 'Loan Amount',
      description: loanAmountTier,
      adjustment: loanAmountAdjustment,
    });
    totalAdjustment += loanAmountAdjustment;
  }

  // 7. Interest Only Adjustment
  if (inputs.amortizationType === 'Interest Only' && llpa.interestOnly) {
    adjustments.push({
      category: 'Amortization',
      description: 'Interest Only',
      adjustment: llpa.interestOnly,
    });
    totalAdjustment += llpa.interestOnly;
  }

  // 8. State Tier Adjustment (Advantage and Plus only)
  if ((programName === 'DSCR Advantage' || programName === 'DSCR Plus') && inputs.propertyState && llpa.stateTier) {
    const stateTier = getStateTier(inputs.propertyState, programName);
    const stateAdjustment = llpa.stateTier[stateTier] || 0;
    if (stateAdjustment !== 0) {
      const tierStates = programName === 'DSCR Advantage' ? 
        'TX, LA, FL, GA, SC, MN, CO, OR, UT, AZ' : 
        'NJ, NY, PA';
      adjustments.push({
        category: 'State Pricing',
        description: stateTier === 'tier1' ? `Tier 1 (${tierStates})` : 'Tier 2 (Other States)',
        adjustment: stateAdjustment,
      });
      totalAdjustment += stateAdjustment;
    }
  }

  // 9. Lock Period Adjustment
  const lockPeriod = inputs.lockPeriod || '30 Days';
  const lockAdjustment = LOCK_PERIOD_ADJUSTMENTS[lockPeriod] || 0;
  if (lockAdjustment !== 0) {
    adjustments.push({
      category: 'Lock Period',
      description: lockPeriod,
      adjustment: lockAdjustment,
    });
    totalAdjustment += lockAdjustment;
  }

  // 10. Extension Days
  const extensionDays = inputs.extensionDays || '0 Days';
  const extensionAdjustment = EXTENSION_FEES[extensionDays] || 0;
  if (extensionAdjustment !== 0) {
    adjustments.push({
      category: 'Extension',
      description: extensionDays,
      adjustment: extensionAdjustment,
    });
    totalAdjustment += extensionAdjustment;
  }

  return {
    adjustments,
    totalAdjustment,
  };
};

/**
 * Find the best rate for a given target price after LLPA adjustments
 * This implements "Max Yield" mode - finding the rate that gives the best YSP
 */
export const findBestRate = (inputs, programName, productType, targetMinPrice = 100, customRateSheet = null) => {
  const rateMatrix = getRateMatrix(programName, productType, customRateSheet);
  const { totalAdjustment } = calculateLLPAAdjustments(inputs, programName);
  
  let bestRate = null;
  let bestPrice = 0;
  let bestAdjustedPrice = 0;
  
  for (const { rate, price } of rateMatrix) {
    const adjustedPrice = price + totalAdjustment;
    
    // Find the rate that gives us the highest price above the minimum target
    if (adjustedPrice >= targetMinPrice && adjustedPrice > bestAdjustedPrice) {
      bestRate = rate;
      bestPrice = price;
      bestAdjustedPrice = adjustedPrice;
    }
  }
  
  // If no rate meets the minimum, return the highest available rate
  if (!bestRate && rateMatrix.length > 0) {
    const sorted = [...rateMatrix].sort((a, b) => b.rate - a.rate);
    bestRate = sorted[0].rate;
    bestPrice = sorted[0].price;
    bestAdjustedPrice = sorted[0].price + totalAdjustment;
  }
  
  return {
    rate: bestRate,
    basePrice: bestPrice,
    adjustedPrice: bestAdjustedPrice,
    totalAdjustment,
  };
};

/**
 * Calculate price for a specific rate selection
 */
export const calculatePriceForRate = (inputs, programName, productType, selectedRate, customRateSheet = null) => {
  const rateMatrix = getRateMatrix(programName, productType, customRateSheet);
  const { adjustments, totalAdjustment } = calculateLLPAAdjustments(inputs, programName);
  
  // Find the base price for the selected rate
  const rateEntry = rateMatrix.find(r => Math.abs(r.rate - selectedRate) < 0.001);
  
  if (!rateEntry) {
    return {
      rate: selectedRate,
      basePrice: 100,
      adjustedPrice: 100 + totalAdjustment,
      adjustments,
      totalAdjustment,
      error: 'Rate not found in matrix',
    };
  }
  
  return {
    rate: selectedRate,
    basePrice: rateEntry.price,
    adjustedPrice: rateEntry.price + totalAdjustment,
    adjustments,
    totalAdjustment,
  };
};

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate all Churchill DSCR metrics
 */
export const calculateChurchillMetrics = (inputs, customRateSheets = {}) => {
  const {
    // Loan Details
    programName = 'DSCR Base',
    productType = '30Y Fixed',
    propertyValue = 0,
    purchasePrice = 0,
    loanAmount = 0,
    
    // Borrower Info
    ficoScore = 740,
    propertyType = 'Single Family',
    loanPurpose = 'Purchase',
    propertyState = 'FL',
    
    // DSCR Info
    dscrRatio = 1.25,
    monthlyRent = 0,
    monthlyTaxes = 0,
    monthlyInsurance = 0,
    monthlyHOA = 0,
    
    // Loan Structure
    amortizationType = 'Fully Amortizing',
    prepayPenalty = '36 Months',
    lockPeriod = '30 Days',
    extensionDays = '0 Days',
    
    // Rate Selection
    maxYieldMode = false,
    selectedRate = null,
    targetMinPrice = 100,
    
    // Broker Comp
    brokerCompPercent = 0,
  } = inputs;

  // Use property value for LTV calculation, fallback to purchase price
  const valueForLTV = propertyValue || purchasePrice;
  const ltv = calculateLTV(loanAmount, valueForLTV);
  
  // Get custom rate sheet if available
  const customRateSheet = customRateSheets[programName] || null;
  
  // Calculate pricing
  let pricingResult;
  if (maxYieldMode || !selectedRate) {
    // Auto-find best rate
    pricingResult = findBestRate(
      { ...inputs, dscrRatio, loanAmount, ficoScore, propertyType, loanPurpose, prepayPenalty, amortizationType, propertyState, lockPeriod, extensionDays },
      programName,
      productType,
      targetMinPrice,
      customRateSheet
    );
  } else {
    // Use selected rate
    pricingResult = calculatePriceForRate(
      { ...inputs, dscrRatio, loanAmount, ficoScore, propertyType, loanPurpose, prepayPenalty, amortizationType, propertyState, lockPeriod, extensionDays },
      programName,
      productType,
      selectedRate,
      customRateSheet
    );
  }
  
  const { adjustments, totalAdjustment } = calculateLLPAAdjustments(
    { ...inputs, dscrRatio, loanAmount, ficoScore, propertyType, loanPurpose, prepayPenalty, amortizationType, propertyState, lockPeriod, extensionDays },
    programName
  );
  
  const finalRate = pricingResult.rate || 7.5;
  const finalPrice = pricingResult.adjustedPrice || 100;
  
  // Calculate payments
  const monthlyPI = calculateMonthlyPI(loanAmount, finalRate);
  const monthlyIO = calculateMonthlyIO(loanAmount, finalRate);
  const monthlyPayment = amortizationType === 'Interest Only' ? monthlyIO : monthlyPI;
  
  // Calculate PITIA for DSCR
  const monthlyPITIA = monthlyPayment + (monthlyTaxes || 0) + (monthlyInsurance || 0) + (monthlyHOA || 0);
  const calculatedDSCR = monthlyRent && monthlyPITIA > 0 ? monthlyRent / monthlyPITIA : dscrRatio;
  
  // Calculate YSP (Yield Spread Premium)
  // YSP = (Adjusted Price - Par) * Loan Amount / 100
  const yspPercent = Math.max(finalPrice - 100, 0);
  const yspAmount = (yspPercent * loanAmount) / 100;
  
  // Broker Compensation
  const brokerComp = (loanAmount * brokerCompPercent) / 100;
  
  // Net YSP (after broker comp)
  const netYSP = yspAmount - brokerComp;
  
  // Fixed Fees
  const totalFixedFees = Object.values(FIXED_FEES).reduce((sum, fee) => sum + fee, 0);
  
  // Total Closing Costs (simplified)
  const totalClosingCosts = totalFixedFees;
  
  // Down Payment (for purchases)
  const downPayment = loanPurpose === 'Purchase' ? (purchasePrice - loanAmount) : 0;
  
  // Cash to Close
  const cashToClose = downPayment + totalClosingCosts;
  
  return {
    // Input Echoes
    programName,
    productType,
    propertyValue: valueForLTV,
    purchasePrice,
    loanAmount,
    ficoScore,
    propertyType,
    loanPurpose,
    propertyState,
    amortizationType,
    prepayPenalty,
    lockPeriod,
    extensionDays,
    
    // Calculated Metrics
    ltv,
    dscrRatio: calculatedDSCR,
    monthlyRent,
    monthlyTaxes,
    monthlyInsurance,
    monthlyHOA,
    monthlyPITIA,
    
    // Rate & Pricing
    selectedRate: finalRate,
    basePrice: pricingResult.basePrice,
    adjustedPrice: finalPrice,
    
    // LLPA Breakdown
    llpaAdjustments: adjustments,
    totalLLPAAdjustment: totalAdjustment,
    
    // Payment Calculations
    monthlyPI,
    monthlyIO,
    monthlyPayment,
    
    // Compensation
    yspPercent,
    yspAmount,
    brokerCompPercent,
    brokerComp,
    netYSP,
    
    // Fees
    fixedFees: FIXED_FEES,
    totalFixedFees,
    totalClosingCosts,
    
    // Cash Requirements
    downPayment,
    cashToClose,
    
    // Rate Options for dropdown
    availableRates: getRateMatrix(programName, productType, customRateSheet),
    
    // Mode
    maxYieldMode,
  };
};

export default calculateChurchillMetrics;
